import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Circle, Polygon, useMap } from 'react-leaflet';
import L from 'leaflet';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import 'leaflet/dist/leaflet.css';
import Header from '../components/Layout/Header';
import {
  getDevices, getGeofences, createGeofence, updateGeofence, deleteGeofence,
  Geofence, GeofenceInput, CircleGeometry, PolygonGeometry,
} from '../services/api';
import { Device } from '../types';
import './Geofences.css';

type DrawType = 'circle' | 'polygon' | null;

const PALETTE = ['#4a6fa5', '#e07a5f', '#81b29a', '#f2cc8f', '#3d405b', '#c44d58', '#6a8e7f'];
const randomColor = () => PALETTE[Math.floor(Math.random() * PALETTE.length)];

const GeomanControls: React.FC<{
  drawType: DrawType;
  onDrawn: (type: Exclude<DrawType, null>, layer: any) => void;
}> = ({ drawType, onDrawn }) => {
  const map = useMap();
  const onDrawnRef = useRef(onDrawn);
  onDrawnRef.current = onDrawn;

  useEffect(() => {
    const handler = (e: any) => {
      const t: Exclude<DrawType, null> = e.shape === 'Circle' ? 'circle' : 'polygon';
      onDrawnRef.current(t, e.layer);
      map.removeLayer(e.layer);
    };
    map.on('pm:create', handler);
    return () => { map.off('pm:create', handler); };
  }, [map]);

  useEffect(() => {
    if (drawType === 'circle') {
      map.pm.enableDraw('Circle', { snappable: false });
    } else if (drawType === 'polygon') {
      map.pm.enableDraw('Polygon', { snappable: false });
    } else {
      map.pm.disableDraw();
    }
  }, [drawType, map]);

  return null;
};

const extractGeometry = (
  type: Exclude<DrawType, null>,
  layer: any,
): CircleGeometry | PolygonGeometry => {
  if (type === 'circle') {
    const c = layer.getLatLng();
    return { center: [c.lat, c.lng], radius: layer.getRadius() };
  }
  const ring = layer.getLatLngs()[0] as L.LatLng[];
  return { points: ring.map(ll => [ll.lat, ll.lng] as [number, number]) };
};

const Geofences: React.FC = () => {
  const [geofences, setGeofences] = useState<Geofence[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [drawType, setDrawType] = useState<DrawType>(null);
  const [draft, setDraft] = useState<GeofenceInput | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { void loadData(); }, []);

  const loadData = async () => {
    try {
      const [gs, ds] = await Promise.all([getGeofences(), getDevices()]);
      setGeofences(gs);
      setDevices(ds);
    } catch (err) {
      console.error('Failed to load geofences/devices:', err);
    }
  };

  const handleDrawn = (type: Exclude<DrawType, null>, layer: any) => {
    setDraft({
      name: '',
      geometry_type: type,
      geometry: extractGeometry(type, layer),
      color: randomColor(),
      notify_enter: true,
      notify_exit: true,
      active: true,
      device_ids: [],
    });
    setEditingId(null);
    setDrawType(null);
  };

  const handleEdit = (g: Geofence) => {
    setDraft({
      name: g.name,
      geometry_type: g.geometry_type,
      geometry: g.geometry,
      color: g.color,
      notify_enter: g.notify_enter,
      notify_exit: g.notify_exit,
      active: g.active,
      device_ids: g.device_ids,
    });
    setEditingId(g.id);
  };

  const handleDelete = async (g: Geofence) => {
    if (!window.confirm(`Usunąć strefę "${g.name}"?`)) return;
    try {
      await deleteGeofence(g.id);
      await loadData();
    } catch (err: any) {
      alert('Błąd usuwania: ' + (err?.response?.data?.detail ?? err?.message ?? err));
    }
  };

  const handleSave = async () => {
    if (!draft || !draft.name.trim() || saving) return;
    setSaving(true);
    try {
      if (editingId != null) {
        await updateGeofence(editingId, draft);
      } else {
        await createGeofence(draft);
      }
      setDraft(null);
      setEditingId(null);
      await loadData();
    } catch (err: any) {
      alert('Błąd zapisu: ' + (err?.response?.data?.detail ?? err?.message ?? err));
    } finally {
      setSaving(false);
    }
  };

  const closeModal = () => {
    setDraft(null);
    setEditingId(null);
  };

  const toggleDevice = (id: number, checked: boolean) => {
    if (!draft) return;
    const ids = new Set(draft.device_ids ?? []);
    if (checked) ids.add(id); else ids.delete(id);
    setDraft({ ...draft, device_ids: Array.from(ids) });
  };

  return (
    <div className="geofences-page">
      <Header />
      <div className="geofences-body">
        <aside className="geofences-sidebar">
          <h2>Strefy ({geofences.length})</h2>
          <div className="draw-buttons">
            <button
              className={drawType === 'circle' ? 'active' : ''}
              onClick={() => setDrawType(drawType === 'circle' ? null : 'circle')}
            >⭕ Okrąg</button>
            <button
              className={drawType === 'polygon' ? 'active' : ''}
              onClick={() => setDrawType(drawType === 'polygon' ? null : 'polygon')}
            >⬢ Polygon</button>
            {drawType && <button onClick={() => setDrawType(null)}>Anuluj</button>}
          </div>
          {drawType && (
            <div className="draw-hint">
              {drawType === 'circle'
                ? 'Kliknij środek i rozciągnij promień'
                : 'Klikaj wierzchołki, kliknij pierwszy aby zamknąć'}
            </div>
          )}
          <ul className="geofences-list">
            {geofences.length === 0 && <li className="empty">Brak stref. Dodaj pierwszą.</li>}
            {geofences.map(g => (
              <li key={g.id} className={!g.active ? 'inactive' : ''}>
                <span className="color-dot" style={{ background: g.color }} />
                <div className="info">
                  <div className="name">{g.name}</div>
                  <div className="meta">
                    {g.geometry_type === 'circle' ? '⭕' : '⬢'} ·
                    {' '}{g.device_ids.length} pojazd{g.device_ids.length === 1 ? '' : 'ów'}
                    {!g.active && ' · wył.'}
                  </div>
                </div>
                <button title="Edytuj" onClick={() => handleEdit(g)}>✏️</button>
                <button title="Usuń" onClick={() => handleDelete(g)}>🗑️</button>
              </li>
            ))}
          </ul>
        </aside>
        <main className="geofences-map">
          <MapContainer
            center={[51.2590266, 22.4958933]}
            zoom={13}
            maxZoom={19}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="© OpenStreetMap"
            />
            <GeomanControls drawType={drawType} onDrawn={handleDrawn} />
            {geofences.map(g => {
              if (!g.active) return null;
              const opts = { color: g.color, fillOpacity: 0.15, weight: 2 };
              return g.geometry_type === 'circle' ? (
                <Circle
                  key={g.id}
                  center={(g.geometry as CircleGeometry).center}
                  radius={(g.geometry as CircleGeometry).radius}
                  pathOptions={opts}
                />
              ) : (
                <Polygon
                  key={g.id}
                  positions={(g.geometry as PolygonGeometry).points}
                  pathOptions={opts}
                />
              );
            })}
          </MapContainer>
        </main>
      </div>

      {draft && (
        <div className="modal-backdrop" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>{editingId != null ? 'Edytuj strefę' : 'Nowa strefa'}</h3>
            <label className="field">
              <span>Nazwa</span>
              <input
                value={draft.name}
                onChange={e => setDraft({ ...draft, name: e.target.value })}
                autoFocus
                maxLength={100}
              />
            </label>
            <label className="field field-row">
              <span>Kolor</span>
              <input
                type="color"
                value={draft.color ?? '#4a6fa5'}
                onChange={e => setDraft({ ...draft, color: e.target.value })}
              />
            </label>
            <label className="field-check">
              <input
                type="checkbox"
                checked={draft.notify_enter ?? true}
                onChange={e => setDraft({ ...draft, notify_enter: e.target.checked })}
              />
              <span>Alert przy wjeździe</span>
            </label>
            <label className="field-check">
              <input
                type="checkbox"
                checked={draft.notify_exit ?? true}
                onChange={e => setDraft({ ...draft, notify_exit: e.target.checked })}
              />
              <span>Alert przy wyjeździe</span>
            </label>
            <label className="field-check">
              <input
                type="checkbox"
                checked={draft.active ?? true}
                onChange={e => setDraft({ ...draft, active: e.target.checked })}
              />
              <span>Aktywna</span>
            </label>
            <fieldset className="field">
              <legend>Pojazdy ({(draft.device_ids ?? []).length})</legend>
              <div className="geofence-device-list">
                {devices.length === 0 && <span className="empty">Brak pojazdów</span>}
                {devices.map(d => (
                  <label key={d.id} className="device-checkbox">
                    <input
                      type="checkbox"
                      checked={(draft.device_ids ?? []).includes(d.id)}
                      onChange={e => toggleDevice(d.id, e.target.checked)}
                    />
                    <span>{d.name}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="modal-actions">
              <button onClick={closeModal} disabled={saving}>Anuluj</button>
              <button onClick={handleSave} disabled={!draft.name.trim() || saving}>
                {saving ? 'Zapisywanie...' : 'Zapisz'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Geofences;
